import React, { useRef, useState, useEffect } from 'react';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import StopIcon from '@material-ui/icons/Stop';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import useLocalAudioToggle from '../../../hooks/useLocalAudioToggle/useLocalAudioToggle';
import { useToast, ToastSeverity } from '../../../components/ToastProvider/ToastProvider';
import { RemoteParticipant, Room } from 'twilio-video';
import { TwilioError } from 'twilio-video';

const ScreenRecorderButton: React.FC = () => {
  const { room } = useVideoContext();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const micTrackRef = useRef<MediaStreamTrack | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const [recording, setRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [disableRecording, setDisableRecording] = useState<boolean>(() => !room || room.participants.size < 1);

  const [isAudioEnabled] = useLocalAudioToggle();
  const { showToast } = useToast();

  useEffect(() => {
    if (micTrackRef.current) {
      micTrackRef.current.enabled = isAudioEnabled;
    }
  }, [isAudioEnabled]);

  useEffect(() => {
    if (!room) return;

    let localReconnectTimeout: NodeJS.Timeout | null = null;
    let participantReconnectTimeout: NodeJS.Timeout | null = null;

    const handleParticipantConnected = () => {
      setDisableRecording(false);
    };

    const handleParticipantDisconnected = () => {
      console.warn('🚪 Participante saiu da sala');

      if (recording && mediaRecorderRef.current?.state === 'recording') {
        stopRecording('A gravação foi interrompida porque o segurado saiu da chamada.', 'warning');
      }

      setDisableRecording(true);
    };

    const handleParticipantReconnecting = (participant: RemoteParticipant) => {
      console.warn('⚠️ Participante reconectando...', participant.identity);

      participantReconnectTimeout = setTimeout(() => {
        console.error('❌ Participante não reconectou. Parando gravação...');
        if (recording && mediaRecorderRef.current?.state === 'recording') {
          stopRecording('A gravação foi interrompida porque o participante caiu da chamada.', 'warning');
        }
      }, 15000);
    };

    const handleParticipantReconnected = (participant: RemoteParticipant) => {
      console.log('✅ Participante reconectado:', participant.identity);
      if (participantReconnectTimeout) {
        clearTimeout(participantReconnectTimeout);
        participantReconnectTimeout = null;
      }
    };

    const handleRoomReconnecting = () => {
      console.warn('⚠️ Atendente está reconectando...');

      localReconnectTimeout = setTimeout(() => {
        console.error('❌ Atendente não reconectou. Parando gravação...');
        if (recording && mediaRecorderRef.current?.state === 'recording') {
          stopRecording('A gravação foi interrompida porque o atendente caiu da chamada.', 'warning');
        }
      }, 15000);
    };

    const handleRoomReconnected = () => {
      console.log('✅ Atendente reconectado');
      if (localReconnectTimeout) {
        clearTimeout(localReconnectTimeout);
        localReconnectTimeout = null;
      }
    };

    const handleRoomDisconnected = () => {
      console.error('❌ Atendente foi desconectado da sala.');
      if (recording && mediaRecorderRef.current?.state === 'recording') {
        stopRecording('A gravação foi interrompida porque o atendente saiu da chamada.', 'warning');
      }
    };

    // Eventos do participante remoto (segurado)
    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);
    room.on('participantReconnecting', handleParticipantReconnecting);
    room.on('participantReconnected', handleParticipantReconnected);

    // Eventos do atendente (usuário local)
    room.on('reconnecting', handleRoomReconnecting);
    room.on('reconnected', handleRoomReconnected);
    room.on('disconnected', handleRoomDisconnected);

    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
      room.off('participantReconnecting', handleParticipantReconnecting);
      room.off('participantReconnected', handleParticipantReconnected);

      room.off('reconnecting', handleRoomReconnecting);
      room.off('reconnected', handleRoomReconnected);
      room.off('disconnected', handleRoomDisconnected);

      if (localReconnectTimeout) clearTimeout(localReconnectTimeout);
      if (participantReconnectTimeout) clearTimeout(participantReconnectTimeout);
    };
  }, [room, recording]);

  const startRecording = async () => {
    if (!room) return;

    setIsLoading(true);

    try {
      // 1. Captura a tela (vídeo + valida displaySurface)
      let screenStream: MediaStream;
      try {
        screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const videoTrack = screenStream.getVideoTracks()[0];
        const settings = videoTrack.getSettings() as any;

        if (settings.displaySurface !== 'monitor') {
          screenStream.getTracks().forEach(track => track.stop());
          alert('Por favor, selecione a tela inteira, não uma aba ou janela.');
          // showToast('Por favor, selecione a tela inteira, não uma aba ou janela!', 'warning');
          return;
        }
      } catch (err) {
        console.error('Erro ao capturar a tela:', err);
        return;
      }

      // 2. Coleta o microfone local
      let micTrack: MediaStreamTrack | null = null;
      try {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micTrack = micStream.getAudioTracks()[0] || null;
      } catch {
        console.warn('Microfone não disponível ou permissão negada.');
        showToast('Microfone não disponível ou permissão negada', 'warning');
      }

      // 3. Inicia contexto de áudio e cria stream combinada para microfone + áudio remoto
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // 4. Conecta microfone ao destino
      if (micTrack) {
        micTrack.enabled = isAudioEnabled;
        micTrackRef.current = micTrack;
        const micSource = audioContext.createMediaStreamSource(new MediaStream([micTrack]));
        micSource.connect(destination);
      }

      // 5. Coleta todas as audio tracks remotas e conecta ao destino
      const remoteAudioTracks: MediaStreamTrack[] = [];
      room.participants.forEach(participant => {
        participant.audioTracks.forEach(publication => {
          if (publication.track && publication.track.mediaStreamTrack) {
            remoteAudioTracks.push(publication.track.mediaStreamTrack);
          }
        });
      });

      remoteAudioTracks.forEach(track => {
        const remoteSource = audioContext.createMediaStreamSource(new MediaStream([track]));
        remoteSource.connect(destination);
      });

      // 6. Combina vídeo da tela com o áudio mixado (microfone + áudio remoto)
      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      // 7. Cria e inicia o MediaRecorder
      const mediaRecorder = new MediaRecorder(combinedStream);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'gravacao_teleReg.webm';
        a.click();

        micTrackRef.current = null;
      };

      mediaRecorder.start();
      setRecording(true);
      showToast('Gravação iniciada!', 'success');
    } catch (err) {
      console.error('Erro ao iniciar a gravação:', err);
      showToast('Erro ao tentar iniciar a gravação!', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const stopRecording = (msg: string, status: ToastSeverity) => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      showToast(msg, status);
    }
  };

  return (
    <Tooltip
      title={isLoading ? 'Selecione uma tela para gravar' : disableRecording ? 'Aguardando segurado para gravar' : ''}
      disableHoverListener={!(isLoading || disableRecording)} // só ativa tooltip se estiver desabilitado
    >
      <span>
        <Button
          onClick={recording ? () => stopRecording('Gravação concluída.', 'success') : startRecording}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : recording ? (
              <StopIcon style={{ color: '#fff' }} />
            ) : (
              <FiberManualRecordIcon style={{ color: '#707578' }} />
            )
          }
          disabled={isLoading || !!disableRecording}
          variant={recording ? 'contained' : 'text'}
          color={recording ? 'secondary' : 'default'}
        >
          {recording ? 'Parar gravação' : isLoading ? 'Iniciando...' : 'Gravar'}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ScreenRecorderButton;
